import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FaGripVertical, FaMusic, FaEllipsisV } from 'react-icons/fa'
import { useMusic } from '../context/MusicContext'

/** Måste matcha `playlistItemKey` i MusicContext för dnd-id och React key. */
function playlistRowId(item) {
  if (!item) return ''
  if (item.type === 'music') return `music|${item.artist}|${item.title}`
  if (item.type === 'audio-book')
    return `audio-book|${item.book}|${item.chapter}`
  return ''
}

function SortableQueueRow({
  item,
  index,
  API_URL,
  currentTrack,
  onPlay,
  selectionMode,
  selected,
  onToggleSelect,
  menuOpen,
  onMenuToggle,
  onMenuSelectFler,
  onMenuTaBort,
  registerMenuRef,
}) {
  const id = playlistRowId(item)
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: selectionMode || !id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  }

  const isActive =
    (item.type === 'music' &&
      currentTrack?.type === 'music' &&
      currentTrack.title === item.title &&
      currentTrack.creator === item.artist) ||
    (item.type === 'audio-book' &&
      currentTrack?.type === 'audio-book' &&
      currentTrack.title === item.chapter &&
      currentTrack.creator === item.book)

  const coverUrl =
    item.type === 'music'
      ? `${API_URL}/artist/${item.artist}/cover/${item.title}`
      : `${API_URL}/book/${item.book}/cover`

  const primaryLabel =
    item.type === 'music' ? item.title : item.chapter || item.title
  const secondaryLabel = item.type === 'audio-book' ? item.book : null

  const selectAria =
    item.type === 'music'
      ? `Välj ${item.title}`
      : `Välj ${item.chapter || 'kapitel'}`

  const rowMainClick = () => {
    if (selectionMode) {
      onToggleSelect(index)
    } else {
      onPlay(item, index)
    }
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`user-playlist-item ${isActive ? 'active' : ''} ${
        selectionMode && selected ? 'user-playlist-item--selected' : ''
      }`}
    >
      <button
        type="button"
        className="user-playlist-drag-handle"
        ref={setActivatorNodeRef}
        aria-label="Reorder"
        disabled={selectionMode || !id}
        {...attributes}
        {...listeners}
      >
        <FaGripVertical size={18} />
      </button>
      {selectionMode ? (
        <label
          className="user-playlist-select-cell"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(index)}
            aria-label={selectAria}
          />
        </label>
      ) : null}
      <button
        type="button"
        className="user-playlist-row-main"
        onClick={rowMainClick}
      >
        <img
          src={coverUrl}
          alt=""
          className="user-playlist-cover"
          onError={(e) => {
            e.target.style.display = 'none'
            e.target.parentElement?.classList?.add('show-fallback-icon')
          }}
        />
        <span className="fallback-icon user-playlist-fallback-icon">
          <FaMusic size={28} />
        </span>
        <span className="user-playlist-track-meta">
          <span className="user-playlist-track-title">{primaryLabel}</span>
          {secondaryLabel ? (
            <span className="user-playlist-track-subtitle">
              {secondaryLabel}
            </span>
          ) : null}
        </span>
      </button>
      <div
        className="user-playlist-row-menu-wrap"
        ref={(el) => registerMenuRef(index, el)}
      >
        <button
          type="button"
          className="user-playlist-kebab"
          aria-expanded={menuOpen}
          aria-haspopup="true"
          aria-label="Fler alternativ"
          onClick={(e) => {
            e.stopPropagation()
            onMenuToggle(index)
          }}
        >
          <FaEllipsisV size={18} />
        </button>
        {menuOpen ? (
          <ul className="user-playlist-dropdown" role="menu">
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="user-playlist-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation()
                  onMenuSelectFler()
                }}
              >
                Välj fler
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className="user-playlist-dropdown-item user-playlist-dropdown-item--danger"
                onClick={(e) => {
                  e.stopPropagation()
                  onMenuTaBort()
                }}
              >
                Ta bort
              </button>
            </li>
          </ul>
        ) : null}
      </div>
    </li>
  )
}

export default function UserPlaylistPanel() {
  const {
    userPlaylist,
    playSong,
    clearUserPlaylist,
    syncCurrentSongIndexToFallback,
    moveUserPlaylistItem,
    removeUserPlaylistIndices,
    currentTrack,
    API_URL,
  } = useMusic()

  const [menuOpenIndex, setMenuOpenIndex] = useState(null)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIndices, setSelectedIndices] = useState(() => new Set())
  const menuRefs = useRef({})

  const registerMenuRef = useCallback((index, el) => {
    if (el) menuRefs.current[index] = el
    else delete menuRefs.current[index]
  }, [])

  const itemIds = useMemo(
    () => userPlaylist.map((item, i) => playlistRowId(item) || `row-${i}`),
    [userPlaylist],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  useEffect(() => {
    if (menuOpenIndex === null) return
    const onPointerDown = (e) => {
      const wrap = menuRefs.current[menuOpenIndex]
      if (wrap && !wrap.contains(e.target)) {
        setMenuOpenIndex(null)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [menuOpenIndex])

  const handleDragEnd = (event) => {
    if (selectionMode) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = itemIds.indexOf(active.id)
    const newIndex = itemIds.indexOf(over.id)
    if (oldIndex < 0 || newIndex < 0) return
    moveUserPlaylistItem(oldIndex, newIndex)
  }

  const handleClearAll = () => {
    clearUserPlaylist()
    syncCurrentSongIndexToFallback()
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedIndices(new Set())
  }

  const toggleSelect = (index) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const handleRemoveSelected = () => {
    if (selectedIndices.size === 0) return
    removeUserPlaylistIndices(Array.from(selectedIndices))
    exitSelectionMode()
  }

  return (
    <section className="user-playlist-panel" aria-label="User playlist">
      <div className="user-playlist-header">
        <h3 className="user-playlist-title">Din kö</h3>
        <div className="user-playlist-header-actions">
          {selectionMode ? (
            <>
              <button
                type="button"
                className="user-playlist-cancel-btn"
                onClick={exitSelectionMode}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="user-playlist-clear-btn"
                disabled={selectedIndices.size === 0}
                onClick={handleRemoveSelected}
              >
                Rensa Valda
              </button>
            </>
          ) : (
            <button
              type="button"
              className="user-playlist-clear-btn"
              disabled={userPlaylist.length === 0}
              onClick={handleClearAll}
            >
              Rensa kö
            </button>
          )}
        </div>
      </div>
      {userPlaylist.length === 0 ? (
        <p className="user-playlist-empty">
          Inga spår i kön ännu (låtar eller kapitel).
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="user-playlist-list">
              {userPlaylist.map((item, index) => (
                <SortableQueueRow
                  key={playlistRowId(item) || `row-${index}`}
                  item={item}
                  index={index}
                  API_URL={API_URL}
                  currentTrack={currentTrack}
                  onPlay={playSong}
                  selectionMode={selectionMode}
                  selected={selectedIndices.has(index)}
                  onToggleSelect={toggleSelect}
                  menuOpen={menuOpenIndex === index}
                  onMenuToggle={(i) => {
                    setMenuOpenIndex((prev) => (prev === i ? null : i))
                  }}
                  onMenuSelectFler={() => {
                    setSelectionMode(true)
                    setMenuOpenIndex(null)
                  }}
                  onMenuTaBort={() => {
                    removeUserPlaylistIndices([index])
                    setMenuOpenIndex(null)
                  }}
                  registerMenuRef={registerMenuRef}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </section>
  )
}
